import { ensureIsoTimezone } from '@heidi/utils/dates';

import type { LatestOpenableRun, WorkflowRun } from '../../contracts/routines.types';

/** Work chat lists omit link-only rows — a run without a shell has nothing to open. */
export function runHasChatShell(run: { project_id?: string | null }): boolean {
  return Boolean(run.project_id);
}

/** Most recently created run, or null when the list is empty. */
export function pickLatestRun(runs: WorkflowRun[]): WorkflowRun | null {
  if (runs.length === 0) return null;
  let latest: WorkflowRun | null = null;
  let latestTime = -Infinity;
  for (const run of runs) {
    const time = run.created_at ? Date.parse(run.created_at) : 0;
    if (time >= latestTime) {
      latest = run;
      latestTime = time;
    }
  }
  return latest;
}

/** Latest run for each persisted routine, ignoring unbound runs. */
export function latestRunByWorkflowId(
  runs: WorkflowRun[]
): ReadonlyMap<string, WorkflowRun> {
  const grouped = new Map<string, WorkflowRun[]>();
  for (const run of runs) {
    if (!run.source_workflow_id) continue;
    const workflowRuns = grouped.get(run.source_workflow_id) ?? [];
    workflowRuns.push(run);
    grouped.set(run.source_workflow_id, workflowRuns);
  }
  return new Map(
    [...grouped].flatMap(([workflowId, workflowRuns]) => {
      const latest = pickLatestRun(workflowRuns);
      return latest ? [[workflowId, latest]] : [];
    })
  );
}

type WithLatestActivity = {
  created_at?: string | null;
  latest_openable_run?: LatestOpenableRun | null;
};

const NO_ACTIVITY_TIME = Number.NEGATIVE_INFINITY;

/** UTC millis, or {@link NO_ACTIVITY_TIME} when the stamp is missing or unparseable. */
const activityTime = (value: string | null | undefined): number => {
  if (!value) return NO_ACTIVITY_TIME;
  const parsed = Date.parse(ensureIsoTimezone(value));
  return Number.isFinite(parsed) ? parsed : NO_ACTIVITY_TIME;
};

/** Newest first: openable-run time, else created_at. No usable time sorts last. */
const latestActivityAt = (workflow: WithLatestActivity): number => {
  const latest = workflow.latest_openable_run;
  if (latest && runHasChatShell(latest)) {
    const fromRun = activityTime(latest.created_at);
    if (fromRun !== NO_ACTIVITY_TIME) return fromRun;
  }
  return activityTime(workflow.created_at);
};

export function orderWorkflowsByLatestActivity<T extends WithLatestActivity>(
  workflows: readonly T[]
): T[] {
  return workflows
    .map((workflow, index) => ({ workflow, index, at: latestActivityAt(workflow) }))
    .sort((a, b) => b.at - a.at || a.index - b.index)
    .map((entry) => entry.workflow);
}
